import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  model,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { ModalService } from '../../utils/services/modal.service';
import { ModalComponent } from '../modal/modal.component';
import {
  PaymentReason,
  Transaction,
  TransactionStatus,
  TransactionType,
} from '../../schemas/transaction';
import { BusinessService } from '../../services/business.service';
import { PaymentAccount } from '../../schemas/payment_account';
import { FormsModule } from '@angular/forms';
import { BossServicesService } from '../../services/boss-services.service';
import { TranslocoModule } from '@jsverse/transloco';
import { Locale } from '../../schemas/locale';
import { QrCodeComponent } from '../qr-code/qr-code.component';
import { TransactionService } from '../../services/transaction.service';
import { Business } from '../../schemas/business';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'qwiko-new-payment',
  imports: [
    CommonModule,
    ModalComponent,
    FormsModule,
    TranslocoModule,
    QrCodeComponent,
  ],
  templateUrl: './new-payment.component.html',
  styleUrl: './new-payment.component.scss',
})
export class NewPaymentComponent implements OnDestroy {
  modalService = inject(ModalService);
  transactionService = inject(TransactionService);
  businessService = inject(BusinessService);
  userService = inject(UserService);
  services = inject(BossServicesService);

  business = computed<Business | null>(() => {
    return this.businessService.business();
  });

  amountInputRef = viewChild<ElementRef<HTMLInputElement>>('amount_number');
  amountInputRefEffect = effect(() => {
    if (this.amountInputRef() && this.amountInputRef()!.nativeElement) {
      this.amountInputRef()!.nativeElement.focus();
    }
  });
  measureRef = viewChild<ElementRef<HTMLSpanElement>>('measure');

  businessPaymentAccounts = this.businessService.businessPaymentAccounts;
  country = computed<Locale>(() => {
    return this.businessService.business()?.country as Locale;
  });

  loading = model<boolean>(false);

  isRecordPayment = this.services.isRecordPayment;

  servicesEffect = effect(() => {
    if (this.isRecordPayment()) {
      this.modalService.open('new-payment-service');
      this.setDefaultPaymentAccount();
    }
  });

  // isCreatedPaymentEffect = effect(() => {
  //   console.log('this.services.isRecordPayment():', this.services.isRecordPayment());
  // });

  isAddAmount = signal<boolean>(false);
  isAddCustomer = signal<boolean>(false);
  isOffLineMode = signal<boolean>(false);
  isChoosePaymentAccount = signal<boolean>(false);
  isAddPaymentReason = signal<boolean>(false);
  isQRCode = signal<boolean>(false);
  isShowQRCode = signal<boolean>(false);
  isConfirmTransaction = signal<boolean>(false);
  isChangeAccountOnConfirm = signal<boolean>(false);
  hasNotConfirmed = signal<boolean>(false);
  isDoNotAsk = signal<boolean>(false);

  transaction = signal<Transaction | null>(null);

  amount = signal<string | null>('0');
  // amountFormatted = computed<string>(() => {
  //   const amount = parseInt(this.amount() ?? '0');
  //   if (isNaN(amount)) {
  //     return '0';
  //   }
  //   return amount.toLocaleString('en-US');
  // });
  amountToSave = computed<number | null>(() => {
    if (this.amount() == null) {
      return 0;
    }
    const amount = this.amount()!.split(',').join('');
    return parseInt(amount);
  });

  customerNumber = signal<string | null>(null);
  customerNumberFormatted = computed<string>(() => {
    const code = this.country()?.phone_code;
    if (!this.customerNumber()) {
      return '';
    }
    return `${code}${this.customerNumber()}`;
  });

  customerNumberDisplay = computed<string>(() => {
    const code = this.country()?.phone_code;
    const starts_with = this.country()?.starts_with;

    if (this.customerNumberFormatted().startsWith(code!) && starts_with) {
      const index = this.customerNumberFormatted().indexOf(starts_with!);

      if (index !== -1) {
        const sliceIndex = index + starts_with!.length;
        const remainder = this.customerNumberFormatted().slice(sliceIndex);

        return `${starts_with}${remainder}`;
      }
    }

    return this.customerNumberFormatted();
  });

  status = signal<TransactionStatus>(TransactionStatus.INITIATED);
  defaultPaymentAccount = signal<PaymentAccount | null>(null);
  // defaultPaymentAccountEffect = effect(() => {
  //   if (this.defaultPaymentAccount()) {
  //     // this.paymentAccount.set(this.defaultPaymentAccount());
  //     console.log('defaultPaymentAccountEffect');
  //   }
  // });
  defaultPaymentAccountId = computed(() => {
    return this.defaultPaymentAccount()?.id
      ? this.defaultPaymentAccount()?.id
      : null;
  });
  isDefaultAccount = computed(() => {
    if (
      this.paymentAccount() === null ||
      this.defaultPaymentAccountId() === null
    )
      return false;
    return this.paymentAccount()?.id === this.getDefaultAccountId()
      ? true
      : false;
  });
  paymentAccount = signal<PaymentAccount | null>(null);
  paymentReason = signal<PaymentReason | null>(null);

  addAmount() {
    this.resetModalStates();
    this.isAddAmount.set(true);
  }

  choosePaymentAccount() {
    this.resetModalStates();
    this.isChoosePaymentAccount.set(true);
  }

  changePaymentAccount(event: boolean = false) {
    if (this.isConfirmTransaction()) {
      this.isChangeAccountOnConfirm.set(true);
    }
    this.resetModalStates();
    this.isShowQRCode.set(event);
    this.isChoosePaymentAccount.set(true);
    this.hasNotConfirmed.set(true);
  }

  toggleDefaultAccount(event: any) {
    const paymentAccountId = this.paymentAccount()?.id;
    if (paymentAccountId) {
      if (event) {
        localStorage.setItem('defaultPaymentAccountId', paymentAccountId);
        const account = this.getDefaultPaymentAccount();
        this.defaultPaymentAccount.set(account);
      } else {
        localStorage.removeItem('defaultPaymentAccountId');
        this.defaultPaymentAccount.set(null);
      }
    }
  }

  getDefaultAccountId() {
    return localStorage.getItem('defaultPaymentAccountId');
  }

  setDefaultPaymentAccount() {
    if (this.paymentAccount() === null) {
      const account = this.getDefaultPaymentAccount();
      this.paymentAccount.set(account);
      this.defaultPaymentAccount.set(account);
    }
  }

  getDefaultPaymentAccount() {
    const paymentAccount = this.businessPaymentAccounts().find(
      (account) => account.id === this.getDefaultAccountId(),
    );
    return paymentAccount ? paymentAccount : null;
  }

  async transactionConfirmation(event?: boolean) {
    if (this.isShowQRCode()) {
      this.isShowQRCode.set(false);
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    // if (event && this.hasNotConfirmed()) {
    //   await new Promise((resolve) => setTimeout(resolve, 2000));
    // }
    this.resetModalStates();
    this.isConfirmTransaction.set(true);
    this.isChangeAccountOnConfirm.set(false);
    this.hasNotConfirmed.set(true);
    this.loading.set(false);
  }

  cancelTransaction() {
    this.resetModalStates();
    this.status.set(TransactionStatus.CANCELED);
    this.updateTransaction();
  }

  async saveTransaction(event?: boolean) {
    this.loading.set(true);
    const transaction: Transaction = {
      // id: crypto.randomUUID(),
      type: TransactionType.PAYMENT,
      status: TransactionStatus.PENDING,
      amount: this.amountToSave()!.toString(),
      customer_number: this.customerNumberFormatted() || null,
      receiver_status: TransactionStatus.PENDING,
      // receiver: this.userService.user()?.id || null,
      receiver_account:
        this.paymentAccount()?.id || this.defaultPaymentAccount()?.id || null,
      business_receiver: this.business()?.id,
      country: this.country()?.id,
      was_offline: this.isOffLineMode(),
      // date_created: new Date().toISOString(),
    };

    if (!this.hasNotConfirmed()) {
      // console.log('transaction', transaction);
      const transactionResponse =
        await this.transactionService.createTransaction(transaction);
      this.transaction.set(transactionResponse);
    }

    this.loading.set(false);

    // if (event && this.hasNotConfirmed()) {
    //   await new Promise((resolve) => setTimeout(resolve, 3000));
    // }
    // this.transactionConfirmation();
  }

  async confirmTransaction() {
    this.status.set(TransactionStatus.CONFIRMED);
    await this.updateTransaction();
  }

  async updateTransaction(event?: boolean) {
    this.loading.set(true);

    const transaction: Transaction = {
      id: this.transaction()?.id,
      status:
        this.status() === TransactionStatus.INITIATED
          ? this.transaction()?.status
          : this.status(),
      // amount: this.amountToSave()!.toString(),
      // customer_number: this.customerNumberFormatted() || null,
      receiver_account: this.paymentAccount()?.id || null,
      receiver_status: event ? TransactionStatus.PENDING : this.status(),
    };
    await this.transactionService.updateTransaction(transaction);
    if (event) {
      this.transactionConfirmation(event);
      return;
    }

    this.loading.set(false);
    this.resetAllStates();
    this.isRecordPayment.set(true);
  }

  async selectPaymentAccount(account: PaymentAccount | null) {
    this.paymentAccount.set(account);
    this.resetModalStates();
    if (this.isOffLineMode()) {
      if (this.hasNotConfirmed() && !this.isShowQRCode()) {
        this.isConfirmTransaction.set(true);
        this.isChangeAccountOnConfirm.set(false);
        this.hasNotConfirmed.set(false);
        return;
      }

      if (this.isShowQRCode()) {
        this.isQRCode.set(true);
        this.isShowQRCode.set(false);
        return;
      }

      this.isQRCode.set(true);
      return;
    }

    if (this.hasNotConfirmed() && !this.isShowQRCode()) {
      this.isConfirmTransaction.set(true);
      this.isChangeAccountOnConfirm.set(false);
      this.hasNotConfirmed.set(false);
      // await this.updateTransaction(true);
      return;
    }

    if (this.isShowQRCode()) {
      this.isQRCode.set(true);
      this.isShowQRCode.set(false);
      // await this.updateTransaction(true);
      return;
    }

    this.isRecordPayment.set(true);
  }

  // allAccounts() {
  //   this.paymentAccount.set(null);
  //   this.resetModalStates();
  //   if (this.isOffLineMode()) {
  //     if (this.hasNotConfirmed()) {
  //       this.isConfirmTransaction.set(true);
  //       this.isChangeAccountOnConfirm.set(false);
  //       this.hasNotConfirmed.set(false);
  //       return;
  //     }
  //     this.isOffLineMode.set(false);
  //     this.isQRCode.set(true);
  //     return;
  //   }

  //   if (this.hasNotConfirmed()) {
  //     this.isConfirmTransaction.set(true);
  //     this.isChangeAccountOnConfirm.set(false);
  //     this.hasNotConfirmed.set(false);
  //     return;
  //   }
  //   this.isRecordPayment.set(true);
  // }

  addPaymentReason() {
    this.resetModalStates();
    this.isAddPaymentReason.set(true);
  }

  selectPaymentReason(reason: PaymentReason) {
    this.paymentReason.set(reason);
    this.resetModalStates();
    this.isRecordPayment.set(true);
  }

  async openQRCode() {
    await this.saveTransaction();
    this.resetModalStates();
    this.isQRCode.set(true);
  }

  createTransaction() {
    this.closeModal();
  }

  customerNumberChange(event?: any) {
    if (this.customerNumber() === null || this.customerNumber() === '0') {
      return;
    }

    // const starts_with = this.country()?.starts_with;
    let number: string = this.customerNumber()?.split(' ').join('') || '';
    number = number.replace(/[^0-9]/g, '');
    const intNumber: number = parseInt(number);
    const numberString: string = intNumber.toString();

    if (isNaN(intNumber) || intNumber == 0) {
      this.customerNumber.set(null);
      return;
    }
    this.customerNumber.set(numberString);
    return;
  }

  formatCustomerNumberOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.customerNumber.set(pastedText ?? '');
    this.customerNumberChange();
  }

  formatAmount(event?: any) {
    // console.log('event:', event);
    // console.log('amount:', this.amount());
    if (event && event.key == 'Enter') {
      this.back();
    }

    if (this.amount() === null || this.amount() === '0') {
      return;
    }

    let number: string | number = this.amount()!;

    number = number.replace(/[\s,]/g, '');
    number = number.replace(/[^0-9,]/g, '');

    let numberInt = parseInt(number);

    // console.log('number:', number);
    // console.log('numberInt', numberInt);

    if (isNaN(numberInt) || numberInt == 0) {
      // console.log('is 0 or NaN', numberInt);
      this.amount.set(null);
      // console.log('amount after:', this.amount());
      return;
    }

    number = numberInt.toLocaleString('en-US');
    this.amount.set(number);

    return;
  }

  formatOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.amount.set(pastedText ?? '');
    this.formatAmount();
  }

  // transform(event: any) {
  //   if (this.measureRef() && this.amountInputRef()) {
  //     // Add extra padding to prevent text from touching the edges
  //     const width = Math.min(
  //       this.measureRef()!.nativeElement.offsetWidth + 10,
  //       this.amountInputRef()!.nativeElement.parentElement?.offsetWidth ||
  //         Infinity,
  //     );
  //     this.amountInputRef()!.nativeElement.style.width = `${width}px`;
  //   }
  // }

  closeModal() {
    this.resetAllStates();
    this.modalService.closeAll();
  }

  back(modal?: string) {
    if (this.isQRCode() && this.hasNotConfirmed()) {
      this.resetAllStates();
    }

    if (this.isChangeAccountOnConfirm() && this.isChoosePaymentAccount()) {
      this.resetModalStates();
      this.isConfirmTransaction.set(true);
      this.isChangeAccountOnConfirm.set(false);
      return;
    }

    if (this.isConfirmTransaction()) {
      this.resetModalStates();
      this.isQRCode.set(true);
      return;
    }

    if (this.isShowQRCode()) {
      this.resetModalStates();
      this.isQRCode.set(true);
      this.isShowQRCode.set(false);
      return;
    }

    this.resetModalStates();
    // this.services.isRecordPayment.set(false);
    this.isRecordPayment.set(true);
    // if(this.isOffLineMode()) {
    //   this.isOffLineMode.set(false)
    // }
  }

  resetAllStates() {
    this.resetModalStates();
    this.amount.set('0');
    this.paymentAccount.set(null);
    this.defaultPaymentAccount.set(null);
    this.paymentReason.set(null);
    this.customerNumber.set('');
    this.isOffLineMode.set(false);
    this.hasNotConfirmed.set(false);
    this.isShowQRCode.set(false);
    this.isChangeAccountOnConfirm.set(false);
    this.transaction.set(null);
    this.status.set(TransactionStatus.INITIATED);
  }

  resetModalStates() {
    this.isAddAmount.set(false);
    this.isChoosePaymentAccount.set(false);
    this.isConfirmTransaction.set(false);
    this.isAddPaymentReason.set(false);
    this.isQRCode.set(false);
    // this.services.isRecordPayment.set(false);
    this.isRecordPayment.set(false);
  }

  ngOnDestroy(): void {
    this.resetAllStates();
  }
}
